import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { BehaviorSubject, delay, first, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { LgvClientService, LgvTSHelper, LgvUserService, NotificationService } from '../../core/core.module';
import { LgvEmailModel } from '../../data-models/lgv-email-model';
import { LgvModel } from '../../data-models/longevity.model';
import * as EmailAddresses from 'email-addresses';

@Component({
    selector: 'lgv-email-input',
    templateUrl: './email-input.component.html',
    styleUrls: ['./email-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailInputComponent implements OnInit, OnDestroy {
    
    @ViewChild('search') searchElement: ElementRef;

    @Input() defaultContactList: LgvEmailModel.EmailContact[];
    @Output() onEmailListUpdated = new EventEmitter<LgvEmailModel.EmailContact[]>();

    onDestroy$ = new Subject();

    selectedContact: LgvEmailModel.EmailContact;
    emailContactList: LgvEmailModel.EmailContact[] = [];

    agentDTO: LgvModel.AgentDTO;

    currentAgent = '';
    currentCompany = '';
    currentOrganization = '';
    selectedClient = null;

    _form = new UntypedFormControl();

    _isLoading: boolean = false;
    isLoading$ = new BehaviorSubject(this._isLoading);

    _clientResults: LgvModel.UserDTO[] = [];
    clientResults$ = new BehaviorSubject(this._clientResults);

    get validEmailContactList(): LgvEmailModel.EmailContact[] {
        return this.emailContactList.filter(x=> this.hasValidEmailFormat(x));
    }

    constructor(public lgvClientSvc: LgvClientService, 
        private notificationSvc: NotificationService,
        public lgvUserSvc: LgvUserService) { }

    ngOnInit() {
        this.lgvUserSvc.AgentDTO$.pipe(first(f=> !!f)).subscribe(x=>{
            this.agentDTO = x;
            this.init();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['defaultContactList'] && this.defaultContactList) {
          this.emailContactList = this.defaultContactList;
          this.onEmailListUpdated.emit(this.validEmailContactList);
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    init(){
        if(this.defaultContactList?.length > 0){
            this.emailContactList = this.defaultContactList;
            this.onEmailListUpdated.emit(this.validEmailContactList);
        }
        this._form
            .valueChanges
            .pipe(
                tap(() => this.isLoading$.next(true)),
                // tap(txtSearch => {
                    // if (!txtSearch) {
                    //     this.OnClientSelected.emit('') ;
                    //     this.OnClientDTOSelected.emit('')
                    // }
                // }),
                switchMap(txtSearch => this.lgvClientSvc.SearchContacts(
                    !txtSearch ? '' : txtSearch,
                    this.agentDTO.agentInfo.id
                )
                ),
                delay(500),
                tap(() => {
                    this.isLoading$.next(false);
                }),
                takeUntil(this.onDestroy$)
            ).subscribe(contacts => {
                const filteredAndSortedContacts = contacts
                    // Only keep contacts that have an email
                    .filter(contact => !!contact.email)
                    // Sort by firstName, then lastName, then email
                    .sort((a, b) => {
                        const firstNameResult = a.firstName.localeCompare(b.firstName);
                        if (firstNameResult !== 0) return firstNameResult;

                        const lastNameResult = a.lastName.localeCompare(b.lastName);
                        if (lastNameResult !== 0) return lastNameResult;

                        return a.email.localeCompare(b.email);
                    });

                // Emit the filtered and sorted results
                this.clientResults$.next(filteredAndSortedContacts);
            });

        this.setFocusToSearch();
    }

    selectClient(contact: any) {
        const contactsToAdd = this.parseContactsFromInput(contact);
    
        if (contactsToAdd.length !== 0)
        {
            this.addContacts(contactsToAdd);
            this.updateAndClear();
        }
    }
    
    private parseContactsFromInput(contact: any): LgvEmailModel.EmailContact[] {
        if (contact?.email) {
            return [this.createEmailContactFromObject(contact)];
        } else {
            let parsedContacts = this.parseContactsFromString(contact);
            if (parsedContacts?.length === 0){
                parsedContacts = this.parseContactsFromString(contact.replace(/ /g, ','));
            }
            return parsedContacts;
        }
    }
    
    private createEmailContactFromObject(contact: any): LgvEmailModel.EmailContact {
        return {
            id: contact.id,
            name: contact.nickName || (contact.firstName && contact.lastName ? `${contact.firstName} ${contact.lastName}` : ''),
            email: contact.email
        };
    }
    
    private parseContactsFromString(rawInput: string): LgvEmailModel.EmailContact[] {
        const normalizedContact = rawInput
            .replace(/;/g, ',') // semi-colon
            .replace(/\t/g, ',') // tab
            .replace(/\n/g, ',') // newline
            .replace(/\r/g, ','); // return
    
        const parsed = EmailAddresses.parseAddressList(normalizedContact);
        if (!parsed || parsed.length === 0) {
            // If parsing fails, check if the input is a single valid email
            if (LgvTSHelper.isValidEmail(rawInput)) {
                return [{ id: null, name: '', email: rawInput }];
            } else {
                return [];
            }
        }
    
        return parsed.map(addr => ({
            id: null,
            name: addr.name || '',
            email: addr.parts['address'].semantic
        }));
    }
    
    private addContacts(contactsToAdd: LgvEmailModel.EmailContact[]): void {
        contactsToAdd.forEach(newContact => {
            if (LgvTSHelper.isValidEmail(newContact.email)) {
                if (this.clientInList(newContact)) {
                    this.notificationSvc.warn('Client has already been added');
                } else {
                    this.emailContactList.push(newContact);
                }
            }
        });
    }
    
    private updateAndClear(): void {
        this.onEmailListUpdated.emit(this.validEmailContactList);
        this.clearSearch();
    }

    
    clientIndexFromContactList(ec: LgvEmailModel.EmailContact):number {
        // let testIndex = this.emailContactList.findIndex(x =>(!ec.id && x.email === ec.email) || (x.id === ec.id && x.name === ec.name && x.email === ec.email));
        return this.emailContactList.findIndex(x =>(!ec.id && x.email === ec.email) || (x.id === ec.id && x.name === ec.name && x.email === ec.email));
    }

    clientInList(ec: LgvEmailModel.EmailContact):boolean {
        let result = this.clientIndexFromContactList(ec);
        return this.clientIndexFromContactList(ec) !== -1;
    }

    setFocusToSearch(){
        setTimeout(()=>{ 
          this.searchElement.nativeElement.focus();
        },0);  
    }

    removeContactFromList(ec: LgvEmailModel.EmailContact){
        let tmpArray = this.emailContactList;
        let remIndex = this.clientIndexFromContactList(ec);
        if (remIndex > -1) {
            tmpArray.splice(remIndex, 1);
            this.emailContactList = tmpArray;
        }
        this.onEmailListUpdated.emit(this.validEmailContactList);
    }

    hasValidEmailFormat(ec: LgvEmailModel.EmailContact): boolean{
        return LgvTSHelper.isValidEmail(ec.email);
    }

    contactIsSelected(ec: LgvEmailModel.EmailContact): boolean{
        return this.selectedContact && this.selectedContact.id === ec.id && this.selectedContact.name === ec.name && this.selectedContact.email === ec.email
    }

    displayFn(client: LgvModel.UserDTO) {
        if (client) { return `${client.firstName} ${client.lastName}`; }
    }

    clearSearch() {
        if (this._form.value) {
            this._form.setValue('');
            this.clientResults$.next([]);
        }
    }
    
    onBlur(){
        if(this._form.value?.length > 0){
            this.selectClient(this._form.value);
        }
    }

    onKeyDown(event:KeyboardEvent){

        if(this.selectedContact && event.key !== 'Backspace'){
            this.selectedContact = null;
            this.setFocusToSearch();
            return;
        }


        if(event.key === 'Tab' || event.key === 'Escape' || event.key === 'Enter' )
        {
            let origFormValue = this._form.value;

            if(this._form.value?.length > 0){
                this.selectClient(this._form.value);
            }

            // if not form value, allow user to tab out, otherwise, keep the focus on search to add another recipient
            if(event.key !== 'Tab' || origFormValue?.length > 0){
                this.setFocusToSearch();
            }

        }

        if(event.key === 'Backspace' )
            {
                if(this._form.value.length === 0 && this.emailContactList.length > 0)
                {

                    if(this.selectedContact){
                        this.removeContactFromList(this.selectedContact);
                        this.selectedContact = null;
                    }else{
                        this.selectedContact = this.emailContactList[this.emailContactList.length-1];
                    }

                }
            }
    
    }
}
